import React from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Icon,
  Tag,
  VStack,
  HStack,
  useColorModeValue,
  Badge,
  Avatar,
  Divider,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaNotesMedical,
  FaPills,
  FaHeartbeat,
  FaPhoneAlt,
  FaUserMd,
  // FaStar,
  // FaPercent,
  // FaStickyNote,
  // FaClipboardList,
} from 'react-icons/fa';
import { MdBloodtype, MdNotes, MdOutlineWaterDrop } from 'react-icons/md';
import formatDateTime from 'constants/helper/formatDateTime';

const MotionBox = motion(Box);

const DetailCard = ({ title, icon, children, bg }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const titleColor = useColorModeValue('gray.700', 'white');

  return (
    <Box
      bg={cardBg}
      p={5}
      borderRadius="2xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="sm"
      height="100%"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="-10px"
        right="-10px"
        bg={bg}
        opacity={0.1}
        borderRadius="full"
        boxSize="80px"
      />
      <Flex align="center" mb={4} gap={3}>
        <Flex
          align="center"
          justify="center"
          bg={bg}
          color="white"
          borderRadius="lg"
          boxSize="36px"
          boxShadow="md"
        >
          <Icon as={icon} boxSize={4} />
        </Flex>
        <Text fontWeight="bold" fontSize="md" color={titleColor}>
          {title}
        </Text>
      </Flex>
      {children}
    </Box>
  );
};

const MedicineBottle = ({ bottle }) => {
  const bg = useColorModeValue(`${bottle.color}.50`, `${bottle.color}.900`);
  const borderColor = `${bottle.color}.100`;
  const labelColor = useColorModeValue(
    `${bottle.color}.600`,
    `${bottle.color}.200`,
  );
  const compositionColor = useColorModeValue('gray.700', 'white');

  const composition = [bottle.data?.R1, bottle.data?.R2, bottle.data?.E]
    .filter(Boolean)
    .join(' + ');

  return (
    <Box
      p={3}
      borderWidth="1px"
      borderRadius="xl"
      borderColor={borderColor}
      bg={bg}
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        color={labelColor}
        mb={2}
        textTransform="uppercase"
      >
        {bottle.label}
      </Text>
      {composition ? (
        <Text fontWeight="bold" color={compositionColor}>
          {composition}
        </Text>
      ) : (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          Empty
        </Text>
      )}
    </Box>
  );
};

const TreatmentDetailsCard = ({ visit }) => {
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const textStrong = useColorModeValue('gray.800', 'white');

  // Vitals Colors
  const bpBg = useColorModeValue('red.50', 'red.900');
  const bpLabelColor = useColorModeValue('red.500', 'red.200');
  const bpValueColor = useColorModeValue('red.700', 'red.100');

  const sugarBg = useColorModeValue('orange.50', 'orange.900');
  const sugarLabelColor = useColorModeValue('orange.500', 'orange.200');
  const sugarValueColor = useColorModeValue('orange.700', 'orange.100');
  const noteBg = useColorModeValue('blue.50', 'blue.900');
  const noteLabeColor = useColorModeValue('blue.500', 'blue.200');
  const noteValueColor = useColorModeValue('blue.700', 'blue.100');

  // Feedback Colors
  // const scoreBg = useColorModeValue('yellow.50', 'yellow.900');
  // const scoreLabelColor = useColorModeValue('yellow.600', 'yellow.200');
  // const scoreValueColor = useColorModeValue('yellow.800', 'yellow.100');

  // const reliefBg = useColorModeValue('green.50', 'green.900');
  // const reliefLabelColor = useColorModeValue('green.600', 'green.200');
  // const reliefValueColor = useColorModeValue('green.800', 'green.100');

  // const remarkBg = useColorModeValue('gray.50', 'gray.700');

  if (!visit) return null;

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        staggerChildren: 0.1,
      },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <MotionBox
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      key={visit._id} // Important for animation reset
    >
      <Box mb={6} textAlign="center">
        <Badge
          colorScheme="blue"
          variant="solid"
          fontSize="sm"
          px={3}
          py={1}
          borderRadius="full"
          mb={2}
        >
          {formatDateTime(visit.createdAt)}
        </Badge>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {/* PATIENT & DOCTOR INFO */}
        <MotionBox variants={itemVariants}>
          <DetailCard title="Patient" icon={FaUserMd} bg="blue.400">
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Avatar
                  size="sm"
                  name={visit?.patientObjId?.name}
                  bg="blue.100"
                  color="blue.600"
                />
                <Box>
                  <Text fontSize="sm" color={textMuted}>
                    Patient
                  </Text>
                  <Text
                    fontWeight="semibold"
                    color={textStrong}
                    textTransform="capitalize"
                  >
                    {visit?.patientObjId?.name || '-'}
                  </Text>
                </Box>
              </HStack>
              <Divider />
              <HStack>
                <Flex
                  align="center"
                  justify="center"
                  bg="green.100"
                  color="green.600"
                  borderRadius="full"
                  boxSize="32px"
                >
                  <Icon as={FaPhoneAlt} boxSize={3} />
                </Flex>
                <Box>
                  <Text fontSize="sm" color={textMuted}>
                    Contact
                  </Text>
                  <Text fontWeight="semibold" color={textStrong}>
                    {visit?.patientObjId?.mobile || '-'}
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </DetailCard>
        </MotionBox>

        {/* HEALTH VITALS */}
        <MotionBox variants={itemVariants}>
          <DetailCard title="Health Vitals" icon={FaHeartbeat} bg="red.400">
            <SimpleGrid columns={2} spacing={4}>
              <Box bg={bpBg} p={3} borderRadius="xl" textAlign="center">
                <Icon as={MdBloodtype} color="red.400" boxSize={6} mb={1} />
                <Text
                  fontSize="xs"
                  color={bpLabelColor}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Blood Pressure
                </Text>
                <Text fontSize="lg" fontWeight="800" color={bpValueColor}>
                  {visit?.bp || '-'}
                </Text>
              </Box>
              <Box bg={sugarBg} p={3} borderRadius="xl" textAlign="center">
                <Icon
                  as={MdOutlineWaterDrop}
                  color="orange.400"
                  boxSize={6}
                  mb={1}
                />
                <Text
                  fontSize="xs"
                  color={sugarLabelColor}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Sugar Level
                </Text>
                <Text fontSize="lg" fontWeight="800" color={sugarValueColor}>
                  {visit?.sugar || '-'}
                </Text>
              </Box>
            </SimpleGrid>

            <Flex mt={10} align="center" gap={1}>
              <Text
                fontSize="xs"
                color={noteLabeColor}
                fontWeight="bold"
                textTransform="uppercase"
              >
                Notes
              </Text>
              <Icon as={MdNotes} color="blue.400" boxSize={3.5} />
            </Flex>

            <Box bg={noteBg} mt={2} border="1px solid #ccc" p={2} rounded="md">
              <Text
                fontSize="xs"
                color={noteValueColor}
                fontWeight="bold"
                textTransform="uppercase"
              >
                {visit?.notes || '-'}
              </Text>
            </Box>
          </DetailCard>
        </MotionBox>

        {/* DISEASES */}
        <MotionBox variants={itemVariants} gridColumn={{ md: 'span 2' }}>
          <DetailCard
            title={
              visit?.diseaseReliefs?.length > 0
                ? 'Clinical Improvement by Disease'
                : 'Diseases Name'
            }
            icon={FaNotesMedical}
            bg="purple.400"
          >
            {visit?.diseaseReliefs?.length > 0 ? (
              <Flex gap={3} wrap="wrap">
                {visit.diseaseReliefs.map((d, i) => (
                  <Tag
                    key={i}
                    size="lg"
                    borderRadius="xl"
                    variant="subtle"
                    colorScheme="purple"
                    px={4}
                    py={3}
                  >
                    <VStack spacing={1} align="start">
                      <Text fontWeight="bold" lineHeight="1.2">
                        {d?.diseaseId?.diseaseEnglish}
                      </Text>

                      {d?.diseaseId?.diseaseHindi && (
                        <Text fontSize="xs" opacity={0.8}>
                          {d.diseaseId.diseaseHindi}
                        </Text>
                      )}

                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="green.600"
                      >
                        Relief: {d.reliefPercent}%
                      </Text>
                    </VStack>
                  </Tag>
                ))}
              </Flex>
            ) : visit?.diseases?.length > 0 ? (
              <Flex gap={3} wrap="wrap">
                {visit.diseases.map((d, i) => (
                  <Tag
                    key={i}
                    size="lg"
                    borderRadius="xl"
                    variant="subtle"
                    colorScheme="purple"
                    px={4}
                    py={3}
                  >
                    <VStack spacing={1} align="start">
                      <Text fontWeight="bold" lineHeight="1.2">
                        {d?.diseaseEnglish}
                      </Text>

                      {d?.diseaseHindi && (
                        <Text fontSize="xs" opacity={0.8}>
                          {d.diseaseHindi}
                        </Text>
                      )}
                    </VStack>
                  </Tag>
                ))}
              </Flex>
            ) : (
              <Text color={textMuted} fontStyle="italic">
                No diseases recorded.
              </Text>
            )}
          </DetailCard>
        </MotionBox>

        {/* MEDICINE BOTTLES */}
        <MotionBox variants={itemVariants} gridColumn={{ md: 'span 2' }}>
          <DetailCard
            title="Prescribed Medicine (Bottles)"
            icon={FaPills}
            bg="teal.400"
          >
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
              {[
                { label: 'Bottle 1', data: visit?.bottle1, color: 'teal' },
                { label: 'Bottle 2', data: visit?.bottle2, color: 'cyan' },
                { label: 'Bottle 3', data: visit?.bottle3, color: 'blue' },
                { label: 'Bottle 4', data: visit?.bottle4, color: 'green' },
              ].map((bottle, idx) => (
                <MedicineBottle key={idx} bottle={bottle} />
              ))}
            </SimpleGrid>
          </DetailCard>
        </MotionBox>

        {/* TREATMENT FEEDBACK */}
        {/* <MotionBox variants={itemVariants} gridColumn={{ md: 'span 2' }}>
          <DetailCard
            title="Treatment Feedback"
            icon={FaClipboardList}
            bg="orange.400"
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <Box bg={scoreBg} p={3} borderRadius="xl" textAlign="center">
                <Icon as={FaStar} color="yellow.400" boxSize={6} mb={1} />
                <Text
                  fontSize="xs"
                  color={scoreLabelColor}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Score
                </Text>
                <Text fontSize="lg" fontWeight="800" color={scoreValueColor}>
                  {visit?.score !== undefined ? `${visit.score}/5` : '-'}
                </Text>
              </Box>
              <Box bg={reliefBg} p={3} borderRadius="xl" textAlign="center">
                <Icon as={FaPercent} color="green.400" boxSize={6} mb={1} />
                <Text
                  fontSize="xs"
                  color={reliefLabelColor}
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Relief Percent
                </Text>
                <Text fontSize="lg" fontWeight="800" color={reliefValueColor}>
                  {visit?.reliefPercent !== undefined
                    ? `${visit.reliefPercent}%`
                    : '-'}
                </Text>
              </Box>
            </SimpleGrid>

            <Box bg={remarkBg} p={4} borderRadius="xl">
              <HStack align="start" spacing={3}>
                <Icon as={FaStickyNote} color="gray.400" boxSize={5} mt={1} />
                <Box width="100%">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={1}
                  >
                    Remark
                  </Text>
                  <Text fontSize="md" color={textStrong} whiteSpace="pre-wrap">
                    {visit?.remark || 'No remarks added.'}
                  </Text>
                </Box>
              </HStack>
            </Box>
          </DetailCard>
        </MotionBox> */}
      </SimpleGrid>
    </MotionBox>
  );
};

export default TreatmentDetailsCard;
